// mfa.service — verifikasi dua langkah (TOTP) untuk akun Owner.
//
// Kenapa hanya Owner (keputusan Apo): akun Owner memegang uang, hak akses, dan tombol hapus —
// kalau passwordnya bocor, seluruh toko terbuka. Staf lain tetap password saja supaya absen &
// pekerjaan harian tak terhambat aplikasi authenticator.
//
// Alur: daftar (enroll) → tampilkan QR → pengguna memindai di Google Authenticator → masukkan 6 angka
// untuk membuktikan jamnya cocok → aktif. Saat login berikutnya, kode itu diminta setelah password.
import { supabase } from '@core/supabase';

export interface EnrollData { factorId: string; qrSvg: string; secret: string }

/** Mulai pendaftaran TOTP; balikannya QR + kode cadangan untuk diketik manual. */
export async function enrollTotp(): Promise<EnrollData> {
  const sb = supabase();
  // Faktor yang tertinggal 'unverified' dari percobaan sebelumnya akan menolak pendaftaran baru
  // dengan pesan "factor already exists" — bersihkan dulu supaya pengguna tak buntu.
  try {
    const { data } = await sb.auth.mfa.listFactors();
    for (const f of data?.all ?? []) if (f.status !== 'verified') await sb.auth.mfa.unenroll({ factorId: f.id });
  } catch { /* bukan penghalang */ }
  const { data, error } = await sb.auth.mfa.enroll({ factorType: 'totp', friendlyName: `iFiCs ${Date.now()}` });
  if (error || !data) throw new Error(error?.message ?? 'Gagal memulai pendaftaran 2FA.');
  return { factorId: data.id, qrSvg: data.totp.qr_code, secret: data.totp.secret };
}

/** Buktikan kode dari aplikasi authenticator → faktor jadi aktif. */
export async function verifyEnroll(factorId: string, code: string): Promise<void> {
  const kode = (code ?? '').replace(/\D/g, '');
  if (kode.length !== 6) throw new Error('Masukkan 6 angka dari aplikasi authenticator.');
  const { error } = await supabase().auth.mfa.challengeAndVerify({ factorId, code: kode });
  if (error) throw new Error('Kode salah atau kedaluwarsa — coba kode terbaru di aplikasi Anda.');
}

/** Faktor TOTP yang sudah aktif (null = 2FA belum menyala). */
export async function activeFactorId(): Promise<string | null> {
  try {
    const { data } = await supabase().auth.mfa.listFactors();
    return data?.totp?.find((f) => f.status === 'verified')?.id ?? null;
  } catch { return null; }
}

/** Matikan 2FA. Password diminta ulang supaya bukan orang lain yang mematikannya di perangkat terbuka. */
export async function disableTotp(factorId: string): Promise<void> {
  const { error } = await supabase().auth.mfa.unenroll({ factorId });
  if (error) throw new Error(error.message);
}

/**
 * Setelah password benar: apakah masih perlu kode 2FA? Balikannya factorId yang harus dijawab,
 * atau null bila tak perlu. Dasarnya tingkat jaminan sesi (aal1 = baru password, aal2 = sudah 2FA).
 */
export async function pendingFactorId(): Promise<string | null> {
  const sb = supabase();
  const { data, error } = await sb.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error || !data) return null;
  if (data.nextLevel !== 'aal2' || data.currentLevel === 'aal2') return null;
  return activeFactorId();
}

/** Jawab tantangan 2FA saat login. */
export async function verifyLoginCode(factorId: string, code: string): Promise<void> {
  const kode = (code ?? '').replace(/\D/g, '');
  if (kode.length !== 6) throw new Error('Masukkan 6 angka dari aplikasi authenticator.');
  const { error } = await supabase().auth.mfa.challengeAndVerify({ factorId, code: kode });
  if (error) throw new Error('Kode salah atau kedaluwarsa.');
}
